import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReviewCommentCountsState, RiskIq } from '@tri-opra/riskiq-web';
import '@tri-opra/riskiq-web/styles.css';
import { delay } from 'msw';
import {
    ExternalHubCommentsSideBar,
    IOpenCommentsCountChangedMessage,
    COMMENT_COUNT_CHANGED,
    ICommentsSideBarErrorMessage,
    COMMENTS_SIDE_BAR_ERROR,
} from '@components-clean/core/comments-side-bar';
import { IOwnedMessageHubHook } from '@components-clean/core/comments-side-bar/comments-message-hub';
import { FullPageInfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { usePageToolbar } from '@contexts-clean/page-toolbar-context';
import { ComponentNames, useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { useProtocolInsightsAccess } from '@hooks-clean/use-protocol-insights-access';
import { IFormData, ProtocolReviewContext } from '@pages-clean/protocol-insights/risk-iq/interfaces/context';
import useCommentCountQueries from '@pages-clean/protocol-insights/risk-iq/review/comment-count-queries';
import { getRiskIqCommentsAccess } from '@pages-clean/protocol-insights/risk-iq/review/helpers';
import RiskIqCommentsMessageHub, {
    RiskIqEntityIds,
    RiskIqCommentMessage,
} from '@pages-clean/protocol-insights/risk-iq/review/risk-iq-comments-message-hub';
import { useRiskIqAvailability } from '@pages-clean/protocol-insights/risk-iq/review/use-risk-iq-availability';
import {
    SelectedRiskIqItem,
    useRiskIqEventHandler,
} from '@pages-clean/protocol-insights/risk-iq/review/use-risk-iq-event-handler';
import { useEventBus } from '@utils-clean/eventBus';
import { getProtocolInsightsPermissions } from '@utils-clean/protocol-insights-access';
import { CommentEntityType, ProtocolInsightsApp } from 'types/graphql-global-types';
import { NullableStudy } from '@contexts/local-state-context';
import { UserContext } from '@contexts/user-context';
import { getIsActiveField } from '@utils/convertor';
import Settings from '@settings/settings';
import { LoggedInPageProps } from '@config/routes/interfaces';
import './index.scss';

interface IProtocolReviewProps {
    study: NullableStudy | undefined;
}

interface ISelectedReviewState {
    isFinished: boolean;
}

interface CommentCount {
    riqId: string;
    count: number;
}

type LoadedCommentCountsState = Extract<ReviewCommentCountsState, { status: 'loaded' }>;

const Content = ({ study }: IProtocolReviewProps) => {
    const [formData, setFormData] = useState<IFormData>({
        study,
    });
    const { user, token } = useContext(UserContext);
    const { state, dispatch } = usePageToolbar();

    const { getCommentCounts, getCommentCountsById } = useCommentCountQueries(study?.id);
    const protocolDocumentIdRef = useRef<string>(undefined);
    const [reviewCommentCountsState, setReviewCommentCountsState] = useState<ReviewCommentCountsState | undefined>(
        undefined,
    );
    // The latest comment counts update. Each new update waits on this so only one runs at a time
    const commentCountsUpdateRef = useRef<Promise<unknown>>(Promise.resolve());
    // Aborted to stop polling and message updates of the comment counts when the review changes or closes, or the page
    // is left
    const commentCountsAbortControllerRef = useRef<AbortController>(undefined);
    // Aborted when a poll starts, so updates by id created before then are dropped as the poll refreshes every count
    const pollStartedAbortControllerRef = useRef<AbortController>(new AbortController());
    // The review's message hub, set when its counts start loading. Polls clear its queue, and items are queued on it to
    // be refetched
    const messageHubRef = useRef<IOwnedMessageHubHook<RiskIqCommentMessage>>(undefined);

    const { componentHasChanges } = useUnsavedChangesContext();

    // Holds the CtQ/Risk type + RiskIQ id from the last selection-changed event, for feeding to the
    // CtQ/Risk comments component, which sends the RiskIQ id as the comments' parentId.
    const [selectedItem, setSelectedItem] = useState<SelectedRiskIqItem | null>(null);
    const [selectedReviewState, setSelectedReviewState] = useState<ISelectedReviewState | null>(null);
    const [commentsReloadKey, setCommentsReloadKey] = useState<number>(0);
    // True while RiskIQ's Create New Association flow is open; the comments panel is read-only meanwhile.
    const [isCreatingAssociation, setIsCreatingAssociation] = useState<boolean>(false);
    // The item open in the comments sidebar. The sidebar keeps its count up to date, so our own updates leave it alone
    const sidebarItemRef = useRef<SelectedRiskIqItem | null>(null);
    // The open item if its sidebar is showing its error. Our own updates keep it up to date instead of the sidebar
    const sidebarErrorItemIdRef = useRef<string | null>(null);
    // The items the sidebar sent counts for during the current update, and the open item if it already has. Their counts
    // are newer than the counts being fetched
    const sidebarCountItemsRef = useRef<SelectedRiskIqItem[]>([]);

    const StudyHasRAM = study && getIsActiveField('RAM', study.modules);

    const protocolReviewProviderValue = useMemo(
        () => ({
            current: formData,
            setCurrent: setFormData,
            selectedItem,
        }),
        [formData, setFormData, selectedItem],
    );

    const { t } = useTranslation();

    const apiOrigin = Settings.api.riskIqApiOrigin() || undefined;
    const bearerToken = token ?? undefined;
    const riskIqAvailability = useRiskIqAvailability(apiOrigin, bearerToken);

    // Access comes from the user's Trial.OS study team level for the study, not RAM role rights (ADO 90806).
    const { access, loading: accessLoading } = useProtocolInsightsAccess(ProtocolInsightsApp.RISK_IQ, study?.id);
    const { canView, canEdit } = getProtocolInsightsPermissions(access);
    const { showComments: hasCommentsPageAccess, writeEnabled: commentsWriteAccess } = getRiskIqCommentsAccess(
        access,
        selectedReviewState?.isFinished,
    );
    const commentsWriteEnabled = commentsWriteAccess && !isCreatingAssociation;

    let commentEntityType: CommentEntityType = CommentEntityType.RISK_IQ_RISK;
    // Left undefined when nothing is selected so the sidebar falls back to its default "Comments" title.
    let commentsTitle: string | undefined;
    if (selectedItem?.type === 'ctq') {
        commentEntityType = CommentEntityType.RISK_IQ_CTQ;
        commentsTitle = t('protocolInsights:riskIQ.ctqComments');
    } else if (selectedItem?.type === 'risk') {
        commentsTitle = t('protocolInsights:riskIQ.riskComments');
    }

    const showCommentsSidebar = selectedReviewState && state.commentsToggle && hasCommentsPageAccess;

    function toCommentCounts(items: CommentCount[]): Record<string, number> {
        const commentCounts: Record<string, number> = {};
        items.forEach((item) => {
            commentCounts[item.riqId] = item.count;
        });
        return commentCounts;
    }

    // Merges counts into those already loaded, leaving out any skipped items (the sidebar's item keeps its own count up
    // to date)
    const mergeCommentCounts = (
        {
            riskCommentCounts,
            ctqCommentCounts,
        }: { riskCommentCounts: CommentCount[]; ctqCommentCounts: CommentCount[] },
        skipRiqIds: (string | undefined)[] = [],
    ) => {
        const notSkipped = (item: CommentCount) => !skipRiqIds.includes(item.riqId);

        setReviewCommentCountsState((current) => {
            // Updates only run once the counts have loaded, and stop when the review changes or closes
            const loadedCommentCounts = current as LoadedCommentCountsState;
            return {
                ...loadedCommentCounts,
                riskCommentCounts: {
                    ...loadedCommentCounts.riskCommentCounts,
                    ...toCommentCounts(riskCommentCounts.filter(notSkipped)),
                },
                ctqCommentCounts: {
                    ...loadedCommentCounts.ctqCommentCounts,
                    ...toCommentCounts(ctqCommentCounts.filter(notSkipped)),
                },
            };
        });
    };

    // The item whose count the sidebar keeps up to date, which is the open item unless its sidebar is showing its error
    const getSidebarDrivenItem = () =>
        sidebarItemRef.current?.id === sidebarErrorItemIdRef.current ? null : sidebarItemRef.current;

    // The sidebar has the newest counts for the item it has open, and any it sent counts for during the update
    const getSidebarRiqIds = () => [...sidebarCountItemsRef.current.map((item) => item.id), getSidebarDrivenItem()?.id];

    // Called as an update starts. Once the sidebar has sent a count, it drives that item until it closes, so the open item
    // is kept if it's in the list
    const resetSidebarCountItems = () => {
        sidebarCountItemsRef.current = sidebarCountItemsRef.current.filter(
            (item) => item.id === getSidebarDrivenItem()?.id,
        );
    };

    // Queued on the message hub to be refetched, which handles retries for us
    const queueCommentCountRefetch = (item: SelectedRiskIqItem) => {
        messageHubRef.current!.queueUpdates([
            // Keyed by the item, so it's only refetched once
            { id: item.id, parentId: item.id, entityType: item.type },
        ]);
    };

    const refreshCommentCountsById = async ({ riskIds, ctqIds }: RiskIqEntityIds, stopSignal: AbortSignal) => {
        resetSidebarCountItems();
        const commentCounts = await getCommentCountsById(riskIds, ctqIds);

        // The review may have been closed or changed while fetching
        if (stopSignal.aborted) {
            return true;
        }
        if (!commentCounts) {
            return false;
        }

        mergeCommentCounts(commentCounts, getSidebarRiqIds());
        return true;
    };

    const updateCommentCountsById = (entityIds: RiskIqEntityIds) => {
        const stopSignal = commentCountsAbortControllerRef.current!.signal;
        const pollStartedSignal = pollStartedAbortControllerRef.current.signal;
        // Waits for any poll or message update in progress, so they can't overwrite each other
        const update = commentCountsUpdateRef.current.then(() => {
            // The review was closed or changed since this update was created, or a poll started and has already
            // refreshed these counts
            if (stopSignal.aborted || pollStartedSignal.aborted) {
                return true;
            }

            // Drop messages for the sidebar's item before fetching, the sidebar keeps its count up to date
            const sidebarItemId = getSidebarDrivenItem()?.id;
            const riskIds = entityIds.riskIds.filter((id) => id !== sidebarItemId);
            const ctqIds = entityIds.ctqIds.filter((id) => id !== sidebarItemId);
            if (riskIds.length === 0 && ctqIds.length === 0) {
                return true;
            }

            return refreshCommentCountsById({ riskIds, ctqIds }, stopSignal);
        });
        commentCountsUpdateRef.current = update;
        return update;
    };

    const pollCommentCounts = async (isFirstFetch: boolean, stopSignal: AbortSignal) => {
        // Waits for any poll or message update in progress, so they can't overwrite each other
        const poll = commentCountsUpdateRef.current.then(async () => {
            // The review was closed or changed while this poll waited its turn
            if (stopSignal.aborted) {
                return null;
            }

            const protocolDocumentId = protocolDocumentIdRef.current!;
            // This poll refreshes every count, so queued message updates and refetches, and updates by id waiting to
            // run, are no longer needed
            messageHubRef.current!.clearQueuedUpdates();
            pollStartedAbortControllerRef.current.abort();
            pollStartedAbortControllerRef.current = new AbortController();
            resetSidebarCountItems();
            const commentCounts = await getCommentCounts(protocolDocumentId);
            // The review may have been closed or changed while fetching
            if (stopSignal.aborted) {
                return commentCounts;
            }
            if (isFirstFetch) {
                setReviewCommentCountsState(
                    commentCounts
                        ? {
                              protocolDocumentId,
                              status: 'loaded',
                              riskCommentCounts: toCommentCounts(commentCounts.riskCommentCounts),
                              ctqCommentCounts: toCommentCounts(commentCounts.ctqCommentCounts),
                          }
                        : { protocolDocumentId, status: 'error' },
                );
            } else if (commentCounts) {
                mergeCommentCounts(commentCounts, getSidebarRiqIds());
            }
            return commentCounts;
        });
        commentCountsUpdateRef.current = poll;
        const commentCounts = await poll;
        if (isFirstFetch && !commentCounts) {
            return;
        }
        await delay(commentCounts ? 30000 : 5000);
        if (stopSignal.aborted) {
            return;
        }
        // eslint-disable-next-line no-void
        void pollCommentCounts(false, stopSignal);
    };

    useEffect(() => {
        setFormData({
            ...formData,
            study,
        });
    }, [study?.id]);

    useEffect(() => {
        return () => {
            commentCountsAbortControllerRef.current?.abort();
        };
    }, []);

    // A layout effect so it's set as soon as the sidebar opens, before any in-flight update can finish
    useLayoutEffect(() => {
        const previousItem = sidebarItemRef.current;
        sidebarItemRef.current = showCommentsSidebar ? selectedItem : null;
        if (previousItem?.id === sidebarItemRef.current?.id) {
            return;
        }

        // The newly opened sidebar loads again
        sidebarErrorItemIdRef.current = null;

        // Nothing keeps the closed item's count up to date now, so it's refetched
        if (previousItem) {
            queueCommentCountRefetch(previousItem);
        }
    }, [showCommentsSidebar, selectedItem]);

    // The sidebar is stuck on its error, so its item's count is refetched and kept up to date by our own updates
    useEventBus<ICommentsSideBarErrorMessage>(COMMENTS_SIDE_BAR_ERROR, ({ parentId }) => {
        const sidebarItem = sidebarItemRef.current;
        if (!sidebarItem || sidebarItem.id !== parentId) return;
        sidebarErrorItemIdRef.current = sidebarItem.id;
        queueCommentCountRefetch(sidebarItem);
    });

    useEventBus<IOpenCommentsCountChangedMessage>(COMMENT_COUNT_CHANGED, ({ all, allChanged }) => {
        // Counts from a sidebar showing its error are ignored, our own updates drive its item's count
        if (!allChanged || !selectedItem || selectedItem.id === sidebarErrorItemIdRef.current) return;
        if (!sidebarCountItemsRef.current.some((item) => item.id === selectedItem.id)) {
            sidebarCountItemsRef.current.push(selectedItem);
        }
        const commentCount = [{ riqId: selectedItem.id, count: all }];
        mergeCommentCounts({
            riskCommentCounts: selectedItem.type === 'risk' ? commentCount : [],
            ctqCommentCounts: selectedItem.type === 'ctq' ? commentCount : [],
        });
    });

    useLayoutEffect(() => {
        // The route always registers the toggle; show it only for a user who can see comments, with a review open.
        const showCommentsToggle = hasCommentsPageAccess && !!selectedReviewState;
        const hasCommentsToggle = state.components.includes('commentsToggle');
        if (showCommentsToggle && !hasCommentsToggle) {
            dispatch({ type: 'showCommentsToggle', ...state });
            return;
        }
        if (!showCommentsToggle && hasCommentsToggle) {
            dispatch({ type: 'hideCommentsToggle', ...state });
        }
    }, [state.components, selectedReviewState, hasCommentsPageAccess]);

    const handleRiskIqEvent = useRiskIqEventHandler(
        study?.id ?? undefined,
        (item: SelectedRiskIqItem | null) => setSelectedItem(item),
        (protocolDocumentId, isFinished) => {
            setSelectedReviewState({ isFinished });
            commentCountsAbortControllerRef.current?.abort();
            protocolDocumentIdRef.current = protocolDocumentId;
            // Counts are loaded by RiskIqCommentsMessageHub once it has connected
            setReviewCommentCountsState({ protocolDocumentId, status: 'loading' });
        },
        () => {
            setSelectedReviewState(null);
            commentCountsAbortControllerRef.current?.abort();
            setReviewCommentCountsState(undefined);
        },
        () => {
            if (componentHasChanges(ComponentNames.commentsSideBar)) {
                setCommentsReloadKey((prevKey) => prevKey + 1);
            }
        },
        () => setSelectedReviewState({ isFinished: true }),
        () => setIsCreatingAssociation(true),
        () => setIsCreatingAssociation(false),
    );
    if (!StudyHasRAM) return <FullPageInfoField message={t('home:pageDataErrors:cannotAccessModule')} type="Info" />;

    if (accessLoading && access === undefined) return <Loading />;

    // Also reached by direct URL navigation, so RiskIQ must never mount without access.
    if (!canView) return <FullPageInfoField message={t('protocolInsights:riskIQ.noAccess')} type="Info" />;

    if (riskIqAvailability === 'checking') return <Loading />;

    // RiskIQ's backend is switched off for this environment (independently of the UI flag).
    if (riskIqAvailability === 'unavailable')
        return <FullPageInfoField message={t('protocolInsights:riskIQ.unavailable')} type="Info" />;

    const loadCommentCounts = (messageHub: IOwnedMessageHubHook<RiskIqCommentMessage>) => {
        messageHubRef.current = messageHub;
        commentCountsAbortControllerRef.current = new AbortController();
        // eslint-disable-next-line no-void
        void pollCommentCounts(true, commentCountsAbortControllerRef.current.signal);
    };

    const renderCommentsSideBar = (messageHub: IOwnedMessageHubHook<RiskIqCommentMessage>) =>
        showCommentsSidebar && (
            <div style={{ display: 'flex', height: '100%' }}>
                <ExternalHubCommentsSideBar
                    key={commentsReloadKey}
                    studyId={study.id}
                    entityType={commentEntityType}
                    externalMessageHub={{
                        hubConnection: messageHub.hubConnection,
                        connectedAtLeastOnce: messageHub.connectedAtLeastOnce,
                        messageFilter: (message: RiskIqCommentMessage) =>
                            message.parentId === selectedItem?.id && message.entityType === selectedItem?.type,
                    }}
                    parentId={selectedItem?.id ?? undefined}
                    noParentSelectedMessage={t('protocolInsights:riskIQ.noCommentsParentRecordSelected')}
                    writeEnabled={commentsWriteEnabled}
                    title={commentsTitle}
                />
            </div>
        );

    return (
        <div className="protocolReview-container">
            <ProtocolReviewContext.Provider value={protocolReviewProviderValue}>
                <div
                    style={{
                        height: '100%',
                        display: 'grid',
                        gridTemplateColumns: showCommentsSidebar ? 'minmax(0, 1fr) auto' : '100%',
                    }}
                >
                    <div style={{ minWidth: 0, height: '100%', overflowY: 'auto' }}>
                        <RiskIq
                            mode="opra-embedded"
                            bearerToken={bearerToken}
                            apiOrigin={apiOrigin}
                            currentUserEmail={user?.email}
                            hostHasUnsavedSelectionChanges={componentHasChanges(ComponentNames.commentsSideBar)}
                            onAppEvent={handleRiskIqEvent}
                            selectedReviewCommentCountsState={reviewCommentCountsState}
                            studyId={study.id}
                            readOnly={!canEdit}
                        />
                    </div>
                    {reviewCommentCountsState && (
                        <RiskIqCommentsMessageHub
                            key={reviewCommentCountsState.protocolDocumentId}
                            protocolDocumentId={reviewCommentCountsState.protocolDocumentId}
                            loadCommentCounts={loadCommentCounts}
                            commentCountsLoaded={reviewCommentCountsState.status === 'loaded'}
                            onCommentsUpdated={updateCommentCountsById}
                            renderCommentsSideBar={renderCommentsSideBar}
                        />
                    )}
                </div>
            </ProtocolReviewContext.Provider>
        </div>
    );
};

const ProtocolReviewWrapper = ({ study }: IProtocolReviewProps) => {
    const content = <Content study={study} />;

    return <>{content}</>;
};

const ProtocolReview = ({ study }: LoggedInPageProps) => <ProtocolReviewWrapper study={study} />;

export default ProtocolReview;
